import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dossiersAPI, pilotageAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// PALETTE — alignée Lexback (espresso + or signal)
// ============================================================================
const C = {
  text:        '#1d1d1f',
  text2:       '#424245',
  text3:       '#8b8576',
  text4:       '#c7c7c5',
  bg:          '#fafaf8',
  card:        '#ffffff',
  border:      'rgba(0,0,0,0.08)',
  borderLt:    'rgba(0,0,0,0.06)',
  borderXLt:   'rgba(0,0,0,0.04)',
  gold: 'var(--brand-primary, #d9a847)',
  goldText:    '#a07d2c',
  goldBg: 'var(--brand-primary-bg, rgba(217,168,71,0.12))',
  goldBgSoft:  'rgba(217,168,71,0.06)',
  green:       '#10B981',
  greenText:   '#047857',
  greenBg:     'rgba(16,185,129,0.06)',
  greenBorder: 'rgba(16,185,129,0.18)',
  red:         '#EF4444',
  purple:      '#7c3aed',
  trackBg:     '#f4f1ea',
};

// ============================================================================
// CALCUL DES COÛTS (dupliqué depuis Dossiers.js — source de vérité unique)
// ============================================================================
function getDossierCosts(d) {
  if (!d) return { total: 0, inpi: 0, jal: 0 };
  const t = (d.type || '').toLowerCase();
  let inpi = 0, jal = 0;

  if (t.includes('creation') || t === 'creation_sas' || t === 'creation_sarl' || t === 'creation_sasu' || t === 'creation_eurl') {
    inpi = 35.59 + 20.34;
  } else if (t === 'creation_sci') {
    inpi = 66.88 + 20.34;
  } else if (t === 'modification' || t.startsWith('modif')) {
    inpi = 192.01;
  } else if (t === 'dissolution') {
    inpi = 177.01;
  } else if (t === 'radiation') {
    inpi = 9.19;
  } else if (t === 'transfert_siege') {
    inpi = 75;
  } else if (t === 'depot_comptes' || t === 'depot_acte') {
    inpi = 55;
  }

  if (t.includes('creation') || t === 'modification' || t.startsWith('modif') || t === 'dissolution' || t === 'transfert_siege') {
    if (t.includes('creation')) jal = 165;
    else if (t === 'dissolution') jal = 175;
    else if (t === 'transfert_siege') jal = 145;
    else jal = 155;
  }

  return { total: inpi + jal, inpi, jal };
}

// ============================================================================
// DÉLAIS OFFICIELS INPI/GREFFE (jours ouvrés, à jour mai 2026)
// Source : infogreffe.fr (moyennes constatées)
// ============================================================================
// ── Moyennes Lexback (benchmark) ──
// Ces valeurs sont des moyennes calculées sur tous les dossiers traités par
// Lexback, tous clients confondus. Elles servent de référence pour comparer
// les délais d'un cabinet aux performances moyennes de la plateforme.
//
// Sont normalement fournies par GET /api/pilotage/benchmarks au runtime ; ces
// valeurs codées en dur ne sont qu'un fallback si l'endpoint n'est pas dispo.
const LEXBACK_BENCHMARK_GREFFES_DEFAULT = {
  'paris':        10.2,
  'nanterre':     9.6,
  'bobigny':      12.8,
  'créteil':      11.4,
  'creteil':      11.4,
  'versailles':   9.8,
  'lyon':         8.4,
  'lille':        6.9,
  'marseille':    7.5,
  'bordeaux':     7.8,
  'toulouse':     8.6,
  'nantes':       7.3,
  'rennes':       6.7,
  'strasbourg':   8.9,
  'montpellier':  8.4,
  'nice':         9.8,
};
const LEXBACK_BENCHMARK_DEFAULT = 9.2;

function normalizeGreffeName(name) {
  if (!name) return '';
  return String(name).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}
// Lookup dans le map de benchmarks (Lexback réel si dispo, sinon fallback)
function getBenchmark(greffeName, benchmarkMap) {
  const norm = normalizeGreffeName(greffeName);
  if (benchmarkMap && benchmarkMap[norm] != null) return benchmarkMap[norm];
  return LEXBACK_BENCHMARK_GREFFES_DEFAULT[norm] ?? LEXBACK_BENCHMARK_DEFAULT;
}

// ============================================================================
// PÉRIODES
// ============================================================================
const PERIODS = [
  { key: '7j',  label: '7j',  days: 7  },
  { key: '30j', label: '30j', days: 30 },
  { key: '90j', label: '90j', days: 90 },
  { key: 'YTD', label: 'YTD', days: null },
];

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}
function startOfYear() {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1);
}
function formatEUR(n) {
  if (n == null || isNaN(n)) return '— €';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n)) + ' €';
}
function diffDaysExclWeekends(start, end) {
  if (!start || !end) return null;
  const a = new Date(start), b = new Date(end);
  if (isNaN(a) || isNaN(b)) return null;
  return Math.max(0, (b - a) / (1000 * 60 * 60 * 24));
}

// ============================================================================
// COMPOSANTS
// ============================================================================
const Card = ({ children, style = {}, padding = '18px 20px' }) => (
  <div style={{
    background: C.card,
    border: `0.5px solid ${C.border}`,
    borderRadius: '12px',
    padding,
    ...style,
  }}>{children}</div>
);

const KpiCard = ({ dot, label, value, sub, trend }) => (
  <Card padding="14px 16px">
    <div style={{ fontSize: '10.5px', color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: dot }} />
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '6px' }}>
      <span style={{ fontSize: '24px', fontWeight: 600, color: C.text, letterSpacing: '-0.025em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      {trend && (
        <span style={{ fontSize: '11px', fontWeight: 600, color: trend.color || C.green }}>{trend.text}</span>
      )}
    </div>
    {sub && <div style={{ fontSize: '11px', color: C.text3, marginTop: '4px' }}>{sub}</div>}
  </Card>
);

const PeriodToggle = ({ value, onChange }) => (
  <div style={{ display: 'inline-flex', gap: '2px', background: '#f3f3f0', padding: '2px', borderRadius: '7px' }}>
    {PERIODS.map(p => (
      <button
        key={p.key}
        type="button"
        onClick={() => onChange(p.key)}
        style={{
          fontSize: '10.5px',
          padding: '3px 9px',
          color: value === p.key ? C.text : C.text3,
          background: value === p.key ? C.card : 'transparent',
          boxShadow: value === p.key ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
          border: 'none',
          borderRadius: '5px',
          fontWeight: 500,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >{p.label}</button>
    ))}
  </div>
);

const SLABar = ({ label, value, target, unit = 'jours' }) => {
  // Pas de donnée mesurable → affichage neutre "—"
  if (value == null) {
    return (
      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontSize: '12.5px', color: C.text }}>{label}</span>
          <span style={{ fontSize: '13px', fontWeight: 600, color: C.text3, fontVariantNumeric: 'tabular-nums' }}>
            — <span style={{ fontSize: '10.5px', color: C.text3, fontWeight: 400 }}>{unit}</span>
          </span>
        </div>
        <div style={{ background: C.trackBg, borderRadius: '999px', height: '5px' }} />
        <div style={{ fontSize: '10.5px', color: C.text3, marginTop: '3px', fontStyle: 'italic' }}>
          cible {target} {unit} · pas encore de mesure sur cette période
        </div>
      </div>
    );
  }
  const ratio = target > 0 ? value / target : 0;
  const isOver = ratio > 1;
  const isFar = ratio < 0.7;
  const color = isOver ? C.gold : (isFar ? C.green : C.text);
  const widthPct = Math.min(100, ratio * 100);
  const delta = target > 0 ? ((value - target) / target) * 100 : 0;
  let deltaText, deltaColor;
  if (Math.abs(delta) < 2) { deltaText = 'conforme ✓'; deltaColor = C.green; }
  else if (delta < 0) { deltaText = `${Math.round(delta)}% sous SLA ✓`; deltaColor = C.green; }
  else { deltaText = `+${Math.round(delta)}% au-dessus`; deltaColor = C.gold; }

  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '12.5px', color: C.text }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
          {value.toFixed(1).replace('.', ',')} <span style={{ fontSize: '10.5px', color: C.text3, fontWeight: 400 }}>{unit}</span>
        </span>
      </div>
      <div style={{ background: C.trackBg, borderRadius: '999px', height: '5px', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: '999px', background: color, width: `${widthPct}%`, transition: 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: '10.5px', color: C.text3, marginTop: '3px' }}>
        cible {target} {unit} · <span style={{ color: deltaColor, fontWeight: 600 }}>{deltaText}</span>
      </div>
    </div>
  );
};

const GreffeRow = ({ name, dossiersCount, real, benchmark }) => {
  // real peut être null (pas encore de délai mesurable pour ce greffe chez ce cabinet)
  const realDisplay = real != null ? real : null;
  const maxScale = Math.max(realDisplay ?? benchmark, benchmark) * 1.2;
  const realPct = realDisplay != null ? (realDisplay / maxScale) * 100 : 0;
  const benchmarkPct = (benchmark / maxScale) * 100;
  const deltaPct = (realDisplay != null && benchmark > 0) ? ((realDisplay - benchmark) / benchmark) * 100 : null;
  const isFaster = deltaPct != null && deltaPct < -1;
  const isSlower = deltaPct != null && deltaPct > 1;
  const deltaColor = isFaster ? C.green : (isSlower ? C.red : C.text3);
  const deltaText = deltaPct == null
    ? '—'
    : (Math.abs(deltaPct) < 1 ? '≈' : `${deltaPct > 0 ? '+' : ''}${Math.round(deltaPct)}%`);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 90px 90px 70px',
      gap: '12px',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: `0.5px solid ${C.borderXLt}`,
      fontSize: '12px',
    }}>
      <div>
        <div style={{ fontSize: '12.5px', color: C.text, fontWeight: 500 }}>{name}</div>
        <div style={{ fontSize: '10.5px', color: C.text3 }}>{dossiersCount} dossier{dossiersCount > 1 ? 's' : ''}</div>
      </div>
      <div style={{ height: '5px', background: C.trackBg, borderRadius: '999px', position: 'relative' }}>
        {realDisplay != null && (
          <div style={{ height: '100%', background: C.text, borderRadius: '999px', width: `${realPct}%` }} />
        )}
        <div style={{ position: 'absolute', top: '-3px', bottom: '-3px', width: '2px', background: C.gold, left: `${benchmarkPct}%` }} title="Moyenne Lexback (benchmark)" />
      </div>
      <div style={{ fontSize: '11.5px', color: C.text3, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
        {realDisplay != null ? realDisplay.toFixed(1).replace('.', ',') : '—'}j / <span style={{ color: C.text4 }}>{benchmark.toFixed(1).replace('.', ',')}j</span>
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: deltaColor, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {deltaText}
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
export default function Pilotage() {
  const { user } = useAuth();
  const plan = user?.workspace?.plan || 'starter';
  const isPro = plan === 'pro' || plan === 'enterprise';

  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30j');
  // Benchmarks Lexback (moyennes globales tous clients confondus).
  // Forme : { greffes: {paris: 10.2, ...}, traitement: 2.1, signature: 3.4, greffe: 8.6 }
  const [benchmarks, setBenchmarks] = useState(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Fetch dossiers une seule fois (la page filtre côté client par période)
  useEffect(() => {
    let cancelled = false;
    dossiersAPI.list({ limit: 500 })
      .then(r => {
        if (cancelled) return;
        setDossiers(Array.isArray(r.data) ? r.data : []);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    // Fetch benchmarks Lexback (best-effort, fallback sur valeurs hardcodées si fail)
    pilotageAPI.benchmarks()
      .then(r => { if (!cancelled && r?.data) setBenchmarks(r.data); })
      .catch(() => { /* silencieux : on utilise les valeurs par défaut */ });
    return () => { cancelled = true; };
  }, []);

  // Calculs dérivés selon période
  const stats = useMemo(() => {
    const periodObj = PERIODS.find(p => p.key === period);
    const cutoff = periodObj.days != null ? daysAgo(periodObj.days) : startOfYear();

    // ───── KPIs : TOUJOURS sur le mois en cours (1er → aujourd'hui) ─────
    // Indépendant du toggle. Comparaison vs même fenêtre du mois précédent.
    const now = new Date();
    const dayOfMonth = now.getDate();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    monthStart.setHours(0, 0, 0, 0);
    // Mois précédent : du 1er au même jour du mois (ex: 1-25 avril si on est le 25 mai)
    const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    prevMonthStart.setHours(0, 0, 0, 0);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth() - 1, dayOfMonth + 1);
    prevMonthEnd.setHours(0, 0, 0, 0);

    const thisMonth = dossiers.filter(d => {
      if (!d.created_at) return false;
      const dt = new Date(d.created_at);
      return dt >= monthStart;
    });
    const prevMonthSamePeriod = dossiers.filter(d => {
      if (!d.created_at) return false;
      const dt = new Date(d.created_at);
      return dt >= prevMonthStart && dt < prevMonthEnd;
    });

    const total = thisMonth.length;
    const totalPrevious = prevMonthSamePeriod.length;

    // Frais avancés ce mois
    let fraisJAL = 0, fraisGTC = 0;
    let nbJAL = 0, nbGTC = 0;
    thisMonth.forEach(d => {
      const c = getDossierCosts(d);
      if (c.jal > 0) { fraisJAL += c.jal; nbJAL += 1; }
      if (c.inpi > 0) { fraisGTC += c.inpi; nbGTC += 1; }
    });
    const totalFrais = fraisJAL + fraisGTC;

    // Tendance % vs même fenêtre du mois précédent
    let totalFraisPrev = 0;
    prevMonthSamePeriod.forEach(d => {
      const c = getDossierCosts(d);
      totalFraisPrev += c.total;
    });
    const deltaFrais = totalFraisPrev > 0 ? ((totalFrais - totalFraisPrev) / totalFraisPrev) * 100 : null;

    // ───── Le reste (courbe, délais, prévisionnel) : suit le toggle ─────
    const inPeriod = dossiers.filter(d => {
      if (!d.created_at) return false;
      const dt = new Date(d.created_at);
      return dt >= cutoff;
    });

    // Délais moyens
    const delaysTraitement = [];
    const delaysSignature = [];
    const delaysGreffe = [];
    inPeriod.forEach(d => {
      const tt = diffDaysExclWeekends(d.created_at, d.assigne_at || d.assigned_at);
      const ts = diffDaysExclWeekends(d.assigne_at || d.assigned_at, d.signe_at || d.signed_at);
      const tg = diffDaysExclWeekends(d.depose_at || d.deposed_at, d.complete_at || d.completed_at || d.kbis_recu_at);
      if (tt != null && tt < 30) delaysTraitement.push(tt);
      if (ts != null && ts < 30) delaysSignature.push(ts);
      if (tg != null && tg < 60) delaysGreffe.push(tg);
    });
    const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    // Pas de fallback démo : null si pas de données réelles, traité côté affichage
    const delaiTraitement = avg(delaysTraitement);
    const delaiSignature  = avg(delaysSignature);
    const delaiGreffe     = avg(delaysGreffe);

    // Délais par greffe (top 6 par volume) — basés sur les VRAIS dossiers du cabinet,
    // comparés aux moyennes Lexback (benchmark tous clients).
    const benchmarkGreffeMap = benchmarks?.greffes || null;
    const byGreffe = {};
    inPeriod.forEach(d => {
      const greffe = d.greffe_label?.name || d.greffe || null;
      if (!greffe) return;
      const tg = diffDaysExclWeekends(d.depose_at || d.deposed_at, d.complete_at || d.completed_at || d.kbis_recu_at);
      if (!byGreffe[greffe]) byGreffe[greffe] = { delays: [], count: 0 };
      byGreffe[greffe].count += 1;
      if (tg != null && tg < 60) byGreffe[greffe].delays.push(tg);
    });
    let greffes = Object.entries(byGreffe).map(([name, data]) => ({
      name,
      count: data.count,
      real: data.delays.length ? avg(data.delays) : null, // null si pas encore de délai mesurable
      benchmark: getBenchmark(name, benchmarkGreffeMap),
    })).sort((a, b) => b.count - a.count).slice(0, 6);

    // Plus de fallback démo : si pas de dossier réel, on affiche un état vide
    // (le composant gère l'affichage à l'écran).

    // Bilan greffes : gain moyen
    const gainsList = greffes.map(g => (g.real != null && g.benchmark != null) ? g.benchmark - g.real : null).filter(x => x != null && x > 0);
    const gainMoyen = gainsList.length ? gainsList.reduce((a, b) => a + b, 0) / gainsList.length : 0;
    const greffesFaster = greffes.filter(g => g.real != null && g.benchmark != null && g.real < g.benchmark).length;

    // Série temporelle (groupé par jour)
    const days = periodObj.days || 90;
    const buckets = {};
    for (let i = 0; i < days; i++) {
      const d = new Date();
      d.setDate(d.getDate() - (days - 1 - i));
      d.setHours(0, 0, 0, 0);
      buckets[d.toISOString().slice(0, 10)] = { nouveaux: 0, clotures: 0 };
    }
    inPeriod.forEach(d => {
      if (d.created_at) {
        const k = new Date(d.created_at).toISOString().slice(0, 10);
        if (buckets[k]) buckets[k].nouveaux += 1;
      }
      const completedAt = d.complete_at || d.completed_at || d.kbis_recu_at;
      if (completedAt) {
        const k = new Date(completedAt).toISOString().slice(0, 10);
        if (buckets[k]) buckets[k].clotures += 1;
      }
    });
    const seriesLabels = Object.keys(buckets);
    const seriesNouveaux = seriesLabels.map(k => buckets[k].nouveaux);
    const seriesClotures = seriesLabels.map(k => buckets[k].clotures);

    // Prévisionnel : cadence des 30 derniers jours
    const last30 = dossiers.filter(d => d.created_at && new Date(d.created_at) >= daysAgo(30));
    const cadenceJour = last30.length / 30;
    const previsionnelDossiers = Math.round(cadenceJour * 7);
    const enCoursRefac = inPeriod
      .filter(d => !['termine', 'depose', 'rejete', 'archive'].includes(d.status))
      .reduce((sum, d) => sum + getDossierCosts(d).total, 0);

    return {
      total,
      totalPrevious,
      fraisJAL,
      fraisGTC,
      totalFrais,
      nbJAL,
      nbGTC,
      deltaFrais,
      delaiTraitement,
      delaiSignature,
      delaiGreffe,
      // Bout en bout : null si aucune des composantes n'est mesurable
      delaiBoutEnBout: (delaiTraitement != null || delaiSignature != null || delaiGreffe != null)
        ? ((delaiTraitement || 0) + (delaiSignature || 0) + (delaiGreffe || 0))
        : null,
      greffes,
      gainMoyen,
      greffesFaster,
      seriesLabels,
      seriesNouveaux,
      seriesClotures,
      previsionnelDossiers,
      enCoursRefac,
    };
  }, [dossiers, period]);

  // Chart.js — courbe d'activité
  const drawChart = useCallback(() => {
    if (!chartRef.current || !window.Chart) return;
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    const labels = stats.seriesLabels.map((iso, i) => {
      const days = stats.seriesLabels.length;
      return `J-${days - 1 - i}`;
    });

    chartInstanceRef.current = new window.Chart(chartRef.current.getContext('2d'), {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Nouveaux',
            data: stats.seriesNouveaux,
            borderColor: C.gold,
            backgroundColor: 'rgba(217,168,71,0.08)',
            borderWidth: 1.8,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: true,
            tension: 0.35,
          },
          {
            label: 'Clôturés',
            data: stats.seriesClotures,
            borderColor: C.green,
            backgroundColor: 'rgba(16,185,129,0.05)',
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            tension: 0.35,
            borderDash: [4, 3],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: C.text,
            titleFont: { family: 'Inter Tight, sans-serif', size: 11 },
            bodyFont: { family: 'Inter Tight, sans-serif', size: 12 },
            padding: 10,
            cornerRadius: 6,
            displayColors: true,
            boxWidth: 8,
            boxHeight: 8,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: C.text4, font: { family: 'Inter Tight, sans-serif', size: 9.5 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
          },
          y: {
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: { color: C.text4, font: { family: 'Inter Tight, sans-serif', size: 9.5 } },
            beginAtZero: true,
          },
        },
        elements: { line: { cubicInterpolationMode: 'monotone' } },
      },
    });
  }, [stats]);

  useEffect(() => {
    if (loading) return;
    // Charge Chart.js si pas déjà présent
    if (!window.Chart) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
      s.async = true;
      s.onload = () => drawChart();
      document.body.appendChild(s);
    } else {
      drawChart();
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [loading, drawChart]);

  if (loading) {
    return <div style={{ padding: '2rem', color: C.text3, fontSize: '13px' }}>Chargement du pilotage…</div>;
  }

  const monthLabel = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const trendFraisColor = stats.deltaFrais != null
    ? (stats.deltaFrais >= 0 ? C.green : C.red)
    : C.text3;

  return (
    <div style={{ fontFamily: 'Inter Tight, -apple-system, sans-serif', letterSpacing: '-0.011em', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '11px', color: C.gold, letterSpacing: '0.18em', fontWeight: 500, textTransform: 'uppercase', marginBottom: '6px' }}>
            Pilotage du cabinet
          </div>
          <h1 style={{ fontSize: '26px', fontWeight: 600, color: C.text, margin: 0, letterSpacing: '-0.025em' }}>
            Performance & frais avancés
          </h1>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
          <div style={{ fontSize: '9.5px', color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
            Période d'analyse
          </div>
          <PeriodToggle value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* KPIs — toujours sur le mois en cours */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', color: C.text, fontWeight: 600, letterSpacing: '-0.015em' }}>
          Ce mois · {monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
        </div>
        <div style={{ fontSize: '10.5px', color: C.text3 }}>
          du 1<sup>er</sup> au {new Date().getDate()} {new Date().toLocaleDateString('fr-FR', { month: 'long' })}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '24px' }}>
        <KpiCard
          dot={C.text}
          label="Total commandes"
          value={stats.total}
          sub={stats.totalPrevious > 0 ? `vs ${stats.totalPrevious} même période mois précédent` : 'aucune comparaison'}
          trend={stats.totalPrevious > 0 ? {
            text: stats.total >= stats.totalPrevious ? `+${stats.total - stats.totalPrevious}` : `${stats.total - stats.totalPrevious}`,
            color: stats.total >= stats.totalPrevious ? C.green : C.red,
          } : null}
        />
        <KpiCard
          dot={C.gold}
          label="Frais JAL"
          value={formatEUR(stats.fraisJAL)}
          sub={stats.nbJAL > 0 ? `${stats.nbJAL} annonces · ${formatEUR(stats.fraisJAL / stats.nbJAL)} moy.` : '— annonces'}
        />
        <KpiCard
          dot={C.purple}
          label="Frais GTC"
          value={formatEUR(stats.fraisGTC)}
          sub={stats.nbGTC > 0 ? `${stats.nbGTC} dépôts · ${formatEUR(stats.fraisGTC / stats.nbGTC)} moy.` : '— dépôts'}
        />
        <KpiCard
          dot={C.green}
          label="Total frais avancés"
          value={formatEUR(stats.totalFrais)}
          sub="refacturés au coût"
          trend={stats.deltaFrais != null ? {
            text: `${stats.deltaFrais >= 0 ? '+' : ''}${Math.round(stats.deltaFrais)}%`,
            color: trendFraisColor,
          } : null}
        />
      </div>

      {/* Courbe d'activité */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '12px', color: C.text, fontWeight: 600, letterSpacing: '-0.015em', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Activité du cabinet
          <span style={{ fontSize: '11px', color: C.text3, fontWeight: 400 }}>
            Évolution sur {PERIODS.find(p => p.key === period).label} · nouveaux dossiers vs clôtures
          </span>
        </div>
        <div style={{ position: 'relative', height: '180px', marginTop: '8px' }}>
          <canvas ref={chartRef} role="img" aria-label="Courbe d'activité du cabinet" />
        </div>
        <div style={{ display: 'flex', gap: '22px', marginTop: '10px', fontSize: '11px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '9px', height: '2px', background: C.gold }} />
            <span style={{ color: '#5C6B7A' }}>Nouveaux dossiers</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '9px', height: '2px', background: C.green, borderTop: `1px dashed ${C.green}` }} />
            <span style={{ color: '#5C6B7A' }}>Dossiers clôturés</span>
          </span>
        </div>
      </Card>

      {/* Délais + Prévisionnel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px', marginBottom: '24px' }}>

        {/* Délais moyens + détail par greffe */}
        <Card>
          <div style={{ fontSize: '12px', color: C.text, fontWeight: 600, letterSpacing: '-0.015em', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Délais moyens
            <span style={{ fontSize: '11px', color: C.text3, fontWeight: 400 }}>vs SLA cible</span>
          </div>

          <SLABar label="Traitement formaliste Lexback" value={stats.delaiTraitement} target={5} />
          <SLABar label="Délai signature client" value={stats.delaiSignature} target={3} />
          <SLABar label="Délai greffe / INPI moyen" value={stats.delaiGreffe} target={10} />

          {/* Détail par greffe */}
          <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: `0.5px solid ${C.borderLt}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', color: C.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Détail par greffe
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '10px', color: C.text3 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '9px', height: '3px', background: C.text, borderRadius: '1px' }} />Votre délai
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '2px', height: '9px', background: C.gold }} />Moy. Lexback
                </span>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 90px 90px 70px',
              gap: '12px',
              fontSize: '9.5px',
              color: C.text4,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 600,
              padding: '4px 0',
              borderBottom: `0.5px solid ${C.borderLt}`,
            }}>
              <span>Greffe</span>
              <span style={{ textAlign: 'center' }}>Vous</span>
              <span style={{ textAlign: 'center' }}>Moy. Lexback</span>
              <span style={{ textAlign: 'right' }}>Δ</span>
            </div>

            {stats.greffes.length === 0 ? (
              <div style={{ padding: '20px 4px', fontSize: '12px', color: C.text3, fontStyle: 'italic', textAlign: 'center' }}>
                Pas encore de données sur cette période. Créez et clôturez des dossiers pour voir vos délais comparés à la moyenne Lexback.
              </div>
            ) : (
              stats.greffes.map(g => (
                <GreffeRow key={g.name} name={g.name} dossiersCount={g.count} real={g.real} benchmark={g.benchmark} />
              ))
            )}
          </div>

          <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `0.5px solid ${C.borderLt}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', color: C.text3, fontWeight: 500 }}>Délai bout-en-bout moyen</span>
            <span style={{ fontSize: '15px', fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
              {stats.delaiBoutEnBout != null
                ? `${stats.delaiBoutEnBout.toFixed(1).replace('.', ',')} jours`
                : '—'}
            </span>
          </div>
        </Card>

        {/* Prévisionnel */}
        <Card>
          <div style={{ fontSize: '12px', color: C.text, fontWeight: 600, letterSpacing: '-0.015em', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Prévisionnel
            <span style={{ fontSize: '11px', color: C.text3, fontWeight: 400 }}>7 prochains jours</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginTop: '8px' }}>
            <span style={{ fontSize: '30px', fontWeight: 600, color: C.text, letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              ~{stats.previsionnelDossiers}
            </span>
            <span style={{ fontSize: '12px', color: C.text3 }}>dossiers attendus</span>
          </div>
          <div style={{ fontSize: '11px', color: C.text3, marginTop: '4px' }}>basé sur la cadence des 30j</div>

          <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: `0.5px solid ${C.borderLt}` }}>
            <div style={{ fontSize: '10.5px', color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: '10px' }}>
              À encaisser
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
              <span style={{ fontSize: '12.5px', color: C.text }}>Frais avancés en cours</span>
              <span style={{ fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatEUR(stats.enCoursRefac)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderTop: `0.5px solid ${C.borderLt}`, marginTop: '4px', paddingTop: '9px' }}>
              <span style={{ fontSize: '12.5px', color: C.text, fontWeight: 600 }}>Total à refacturer</span>
              <span style={{ fontSize: '15px', fontWeight: 600, color: C.gold, fontVariantNumeric: 'tabular-nums' }}>{formatEUR(stats.enCoursRefac)}</span>
            </div>
          </div>

          {stats.greffesFaster > 0 && stats.gainMoyen > 0 && (
            <div style={{
              marginTop: '14px',
              padding: '9px 11px',
              background: C.greenBg,
              border: `0.5px solid ${C.greenBorder}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}>
              <div style={{ color: C.green, fontSize: '14px', marginTop: '1px' }}>↗</div>
              <div>
                <div style={{ fontSize: '11.5px', color: C.greenText, fontWeight: 600, lineHeight: 1.3 }}>
                  Gain de {stats.gainMoyen.toFixed(1).replace('.', ',')} jours
                </div>
                <span style={{ fontSize: '10.5px', color: '#5C6B7A', lineHeight: 1.4 }}>
                  Vos dossiers sont traités plus vite que la moyenne Lexback sur {stats.greffesFaster} greffe{stats.greffesFaster > 1 ? 's' : ''} sur {stats.greffes.length}.
                </span>
              </div>
            </div>
          )}
        </Card>

      </div>

      {/* Upsell Pro (Starter uniquement) */}
      {!isPro && (
        <Card padding="14px 16px" style={{ background: `linear-gradient(180deg, ${C.bg} 0%, ${C.card} 100%)`, border: `0.5px solid rgba(217,168,71,0.25)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(217,168,71,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px', color: C.gold }}>↗</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>Aller plus loin avec Pilotage Pro</div>
              <div style={{ fontSize: '11.5px', color: '#5C6B7A', marginTop: '2px' }}>Benchmark sectoriel · Cohortes clients · Scorecard qualité · Alertes intelligentes</div>
            </div>
            <Link
              to="/facturation"
              style={{
                background: C.text,
                color: 'white',
                padding: '7px 14px',
                borderRadius: '7px',
                fontSize: '12px',
                fontWeight: 500,
                textDecoration: 'none',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >Découvrir Pro →</Link>
          </div>
        </Card>
      )}

    </div>
  );
}
